'use client'

import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CheckCircle, Spinner, Copy, XCircle } from 'phosphor-react'
import Components from '../components'

export default function SendToken() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [tokenUrl, setTokenUrl] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [copied, setCopied] = useState(false)

  const email = searchParams.get('email') || ''
  const name = searchParams.get('name') || ''

  useEffect(() => {
    // Auto-send token when page loads if email is provided
    if (email && status === 'idle') {
      sendToken()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  const sendToken = async () => {
    if (!email) {
      setStatus('error')
      setErrorMessage('No email address provided')
      return
    }

    setStatus('loading')
    setErrorMessage('')

    try {
      // Handle port routing for local development
      const isDev = import.meta.env.DEV || window.location.hostname === 'localhost'
      const isVitePort = window.location.port === '5174'
      const functionPort = (isDev && isVitePort) ? '8888' : window.location.port
      const baseUrl = isDev && isVitePort 
        ? `http://localhost:${functionPort}`
        : ''
      
      const response = await fetch(`${baseUrl}/.netlify/functions/sendTokenEmail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, name })
      })

      const data = await response.json()
      console.log('SendToken response:', data)

      if (response.ok && data.success) {
        setTokenUrl(data.tokenUrl)
        
        // Store token in localStorage so it can be validated
        if (data.tokenData) {
          const existingTokens = JSON.parse(localStorage.getItem('temp_access_tokens') || '{}')
          // Store token with the correct structure that tempAccessManager expects
          existingTokens[data.tokenData.token] = {
            token: data.tokenData.token,
            expiresAt: data.tokenData.expiresAt,
            uses: data.tokenData.uses || 0,
            maxUses: data.tokenData.maxUses || 100,
            createdAt: data.tokenData.createdAt || Date.now(),
            allowedPaths: data.tokenData.allowedPaths || ['*']
          }
          localStorage.setItem('temp_access_tokens', JSON.stringify(existingTokens))
          
          console.log('✅ Token stored in localStorage:', {
            token: data.tokenData.token,
            expiresAt: new Date(data.tokenData.expiresAt).toISOString(),
            expiresIn: Math.round((data.tokenData.expiresAt - Date.now()) / (1000 * 60 * 60 * 24)) + ' days',
            tokenUrl: data.tokenUrl,
            storedKeys: Object.keys(existingTokens)
          })
          
          // Verify it was stored correctly
          const verify = JSON.parse(localStorage.getItem('temp_access_tokens') || '{}')
          const storedToken = verify[data.tokenData.token]
          console.log('🔍 Verification - token in storage:', !!storedToken)
          if (storedToken) {
            console.log('🔍 Stored token structure:', storedToken)
            console.log('🔍 Token will expire:', new Date(storedToken.expiresAt).toISOString())
            console.log('🔍 Token is valid for:', storedToken.allowedPaths)
          }
          
          // Test token validation immediately
          import('../utils/tempAccess').then(({ tempAccessManager }) => {
            const isValid = tempAccessManager.validateTempToken(data.tokenData.token, '/')
            console.log('🧪 Immediate validation test:', isValid ? '✅ VALID' : '❌ INVALID')
          }).catch(err => {
            console.warn('Could not test token validation:', err)
          })
        }

        // Check if email was actually sent
        if (data.emailSent === false) {
          setStatus('error')
          const errorMsg = data.emailError || data.message || 'Email failed to send. Token URL is shown below.'
          setErrorMessage(errorMsg)
          setTokenUrl(data.tokenUrl) // Still show the URL
          console.error('❌ Email failed to send:', {
            error: data.emailError,
            message: data.message,
            tokenUrl: data.tokenUrl
          })
        } else if (data.emailSent === true) {
          setStatus('success')
          console.log('✅ Email sent successfully:', data.emailId)
        } else {
          // emailSent not specified - assume success if no error
          setStatus('success')
        }
      } else {
        setStatus('error')
        setErrorMessage(data.error || data.message || 'Failed to send token')
        if (data.tokenUrl) {
          setTokenUrl(data.tokenUrl) // Still show the URL even if email failed
        }
      }
    } catch (error) {
      console.error('Error sending token:', error)
      setStatus('error')
      setErrorMessage('Network error. Please try again.')
    }
  }

  const copyToClipboard = async () => {
    if (!tokenUrl) return

    try {
      await navigator.clipboard.writeText(tokenUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  return (
    <Components.PageContainer>
      <Components.GlassNavigation />
      
      <main className="pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-2xl mx-auto">
          <Card className="brutal-panel p-8">
            <h1 className="text-3xl font-bold text-[var(--brutal-ink)] mb-6">Send Access Token</h1>

            {status === 'idle' && (
              <div className="text-center py-8">
                <p className="text-[var(--brutal-muted)] mb-6">
                  {email ? (
                    <>Ready to send a 14-day access token to <strong>{email}</strong></>
                  ) : (
                    'No email address provided. Please provide an email address in the URL.'
                  )}
                </p>
                {email && (
                  <Button
                    onClick={sendToken}
                    className="brutal-btn text-[var(--brutal-ink)]"
                  >
                    Send Token
                  </Button>
                )}
              </div>
            )}

            {status === 'loading' && (
              <div className="text-center py-12">
                <Spinner weight="duotone" className="w-12 h-12 text-[var(--brutal-accent)] mx-auto mb-4 animate-spin" />
                <p className="text-[var(--brutal-muted)]">Generating token and sending email...</p>
              </div>
            )}

            {status === 'success' && (
              <div className="space-y-6">
                <div className="text-center">
                  <CheckCircle weight="duotone" className="w-16 h-16 text-[var(--brutal-success)] mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-[var(--brutal-ink)] mb-2">Token Sent!</h2>
                  <p className="text-[var(--brutal-muted)] mb-2">
                    A 14-day access token has been sent to <strong>{email}</strong>
                  </p>
                  <p className="text-[var(--brutal-muted)] text-sm">
                    The email should arrive within a few minutes. Check spam folder if not received.
                  </p>
                </div>

                {tokenUrl && (
                  <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4">
                    <label className="block text-sm font-medium text-[var(--brutal-ink)] mb-2">
                      Token URL (for your records):
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={tokenUrl}
                        readOnly
                        className="flex-1 bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded px-3 py-2 text-[var(--brutal-ink)] text-sm"
                      />
                      <Button
                        onClick={copyToClipboard}
                        size="sm"
                        variant="outline"
                        className={`bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] ${
                          copied ? 'bg-green-500/20 border-green-400' : ''
                        }`}
                      >
                        {copied ? (
                          <CheckCircle weight="duotone" className="w-4 h-4" />
                        ) : (
                          <Copy weight="duotone" className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-[var(--brutal-muted)] text-xs mt-2">
                      Valid for 14 days • Up to 100 uses
                    </p>
                  </div>
                )}

                <div className="text-center">
                  <Button
                    onClick={() => window.location.href = '/Contact'}
                    variant="outline"
                    className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)]"
                  >
                    Back to Contact
                  </Button>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-6">
                <div className="text-center">
                  <XCircle weight="duotone" className="w-16 h-16 text-red-800 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-[var(--brutal-ink)] mb-2">Error</h2>
                  <p className="text-[var(--brutal-muted)] mb-2">{errorMessage}</p>
                  <p className="text-[var(--brutal-muted)] text-sm">
                    Check the browser console (F12) and Netlify function logs for more details.
                  </p>
                </div>

                {tokenUrl && (
                  <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4">
                    <label className="block text-sm font-medium text-[var(--brutal-ink)] mb-2">
                      Token URL (email failed, but token was generated):
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={tokenUrl}
                        readOnly
                        className="flex-1 bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded px-3 py-2 text-[var(--brutal-ink)] text-sm"
                      />
                      <Button
                        onClick={copyToClipboard}
                        size="sm"
                        variant="outline"
                        className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)]"
                      >
                        <Copy weight="duotone" className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-[var(--brutal-muted)] text-xs mt-2">
                      You can manually send this URL to {email}
                    </p>
                  </div>
                )}

                <div className="text-center">
                  <Button
                    onClick={sendToken}
                    className="brutal-btn text-[var(--brutal-ink)] mr-2"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={() => window.location.href = '/Contact'}
                    variant="outline"
                    className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)]"
                  >
                    Back to Contact
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </main>
    </Components.PageContainer>
  )
}
